import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { locationsAPI } from '../lib/api';
import { BulkImportDialog } from '../components/ui/bulk-import-dialog';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Building2,
  MapPin,
  Phone,
  Mail,
  Upload,
  Download,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Locations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  useEffect(() => {
    loadLocations();
  }, []);

  const handleDuplicate = async (id) => {
    setDuplicatingId(id);
    try {
      const res = await locationsAPI.duplicate(id);
      toast.success(`Standort "${res.data.name}" wurde dupliziert`);
      loadLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Duplizieren');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const res = await locationsAPI.exportCsv(format);
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      const mime = format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `standorte_export.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Export gestartet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    }
  };

  const loadLocations = async () => {
    try {
      const response = await locationsAPI.getAll();
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to load locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const [locationToDelete, setLocationToDelete] = useState(null);

  const handleDelete = async (id) => {
    try {
      await locationsAPI.delete(id);
      setLocations(locations.filter((l) => l.id !== id));
      toast.success('Standort gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const filteredLocations = locations.filter((loc) =>
    loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-32" />
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="locations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Standorte</h1>
            <p className="text-muted-foreground mt-1">{locations.length} Standorte insgesamt</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full"
                  data-testid="export-locations-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Exportieren
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-locations-csv">
                  CSV (.csv)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-locations-xlsx">
                  Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setImportOpen(true)}
              data-testid="import-locations-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              Importieren
            </Button>
            <Link to="/locations/new">
              <Button className="rounded-full" data-testid="add-location-btn">
                <Plus className="mr-2 h-4 w-4" />
                Neuer Standort
              </Button>
            </Link>
          </div>
        </div>

        {/* Search */}
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Name, Stadt oder Adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10"
                data-testid="location-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-border/60">
          <CardContent className="p-0">
            {filteredLocations.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="data-table">
                    <TableHead>Name</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead className="hidden md:table-cell">Kontakt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLocations.map((location, index) => (
                    <TableRow
                      key={location.id}
                      className="animate-slide-up"
                      style={{ animationDelay: `${index * 0.02}s` }}
                      data-testid={`location-row-${location.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Building2 className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <span className="font-medium">{location.name}</span>
                            {location.short_id && (
                              <div className="text-xs font-mono text-muted-foreground">
                                {location.short_id}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {location.address}, {location.postal_code} {location.city}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="space-y-1">
                          {location.phone && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {location.phone}
                            </div>
                          )}
                          {location.email && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {location.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`location-actions-${location.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/locations/${location.id}/edit`)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDuplicate(location.id)}
                              disabled={duplicatingId === location.id}
                              data-testid={`duplicate-location-${location.id}`}
                            >
                              <Copy className="mr-2 h-4 w-4" />
                              {duplicatingId === location.id ? 'Wird dupliziert...' : 'Duplizieren'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setLocationToDelete(location)}
                              data-testid={`delete-location-${location.id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium text-lg">Keine Standorte gefunden</h3>
                <p className="text-muted-foreground mt-1">
                  {searchTerm ? 'Versuchen Sie andere Suchbegriffe' : 'Erstellen Sie den ersten Standort'}
                </p>
                {!searchTerm && (
                  <Link to="/locations/new">
                    <Button className="mt-4 rounded-full">
                      <Plus className="mr-2 h-4 w-4" />
                      Standort anlegen
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <BulkImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          title="Standorte importieren"
          downloadTemplate={() => locationsAPI.importTemplate()}
          runImport={(file, dryRun) => locationsAPI.import(file, dryRun)}
          templateFilename="standorte_vorlage.csv"
          onSuccess={loadLocations}
        />

        <ConfirmDeleteDialog
          open={!!locationToDelete}
          onOpenChange={(o) => !o && setLocationToDelete(null)}
          title="Standort löschen?"
          description={
            locationToDelete
              ? `Möchten Sie den Standort „${locationToDelete.name}“ wirklich löschen? Diese Aktion wird in den Zugriffsprotokollen vermerkt.`
              : ''
          }
          onConfirm={() => {
            if (locationToDelete) handleDelete(locationToDelete.id);
            setLocationToDelete(null);
          }}
          testIdPrefix="confirm-delete-location"
        />
      </div>
    </DashboardLayout>
  );
}
